// Public buyer-facing room view.
// Route: /r/{token} — no auth required.
// Mirrors the "Preview" mode of the builder but standalone.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import RichTextEditor from "../components/RichTextEditor";
import { Loader2, DoorOpen, Mail } from "lucide-react";
import { toast } from "sonner";

const VISITOR_KEY_PREFIX = "pragma_room_visitor:";

function toEmbedUrl(raw) {
  if (!raw) return "";
  try {
    const u = new URL(raw);
    if (u.hostname.includes("loom.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      return `https://www.loom.com/embed/${id}`;
    }
    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* invalid */ }
  return "";
}

export default function PublicRoom() {
  const { token } = useParams();
  const [room, setRoom] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [error, setError] = useState(null);
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const visitorRef = useRef(null);

  const storageKey = `${VISITOR_KEY_PREFIX}${token}`;

  const fetchRoom = useCallback(async () => {
    try {
      const params = visitorRef.current ? `?visitor_id=${encodeURIComponent(visitorRef.current)}` : "";
      const { data } = await api.get(`/public/rooms/${token}${params}`);
      setRoom(data);
      if (!data.requires_email && data.sections?.length && !activeSection) {
        setActiveSection(data.sections[0].id);
      }
    } catch (e) {
      setError(e.response?.status === 404 ? "not_found" : "error");
    }
  }, [token, activeSection]);

  const track = useCallback(async (event, sectionId) => {
    if (!visitorRef.current && room?.access_type === "email_gated") return;
    try {
      const body = { event, visitor_id: visitorRef.current, section_id: sectionId };
      const r = await api.post(`/public/rooms/${token}/track`, body);
      if (r.data?.visitor_id && !visitorRef.current) {
        visitorRef.current = r.data.visitor_id;
        try { localStorage.setItem(storageKey, r.data.visitor_id); } catch {}
      }
    } catch { /* silent */ }
  }, [token, room, storageKey]);

  useEffect(() => {
    try { visitorRef.current = localStorage.getItem(storageKey); } catch {}
    fetchRoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // After room loads (and is not gated), fire room_open + section_view tracking.
  useEffect(() => {
    if (!room || room.requires_email) return;
    track("room_open");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.id, room?.requires_email]);

  useEffect(() => {
    if (activeSection && room && !room.requires_email) track("section_view", activeSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const submitEmail = async (email, name) => {
    setSubmittingEmail(true);
    try {
      const { data } = await api.post(`/public/rooms/${token}/verify-email`, { email, name });
      visitorRef.current = data.visitor_id;
      try { localStorage.setItem(storageKey, data.visitor_id); } catch {}
      // Re-fetch the full room now that we're verified
      await fetchRoom();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Please enter a valid email");
    } finally { setSubmittingEmail(false); }
  };

  const brandColor = room?.theme_color || "#5B4FE8";
  const isDark = room?.theme_mode === "dark";
  const bg = isDark ? "#0f172a" : "#ffffff";
  const surface = isDark ? "#1e293b" : "#ffffff";
  const textColor = isDark ? "#e2e8f0" : "#0f172a";
  const textMute = isDark ? "#94a3b8" : "#64748b";
  const border = isDark ? "#334155" : "#e5e7eb";

  const section = useMemo(
    () => room?.sections?.find((s) => s.id === activeSection),
    [room, activeSection]
  );

  if (error === "not_found") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fafafa" }}>
        <div style={{ textAlign: "center" }}>
          <DoorOpen size={40} style={{ margin: "0 auto 14px", color: "#94a3b8" }} />
          <h1 style={{ fontSize: 22, fontWeight: 600 }}>Room not found</h1>
          <p style={{ color: "#64748b", marginTop: 6 }}>This link may have expired or the room was unpublished.</p>
        </div>
      </div>
    );
  }
  if (!room) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }}>
        <Loader2 className="animate-spin" size={22} /> <span style={{ marginLeft: 10 }}>Loading room…</span>
      </div>
    );
  }

  if (room.requires_email) {
    return <EmailGate room={room} onSubmit={submitEmail} submitting={submittingEmail} />;
  }

  return (
    <div style={{ minHeight: "100vh", background: bg, color: textColor, display: "flex" }}>
      {/* Sidebar */}
      <aside style={{
        width: 240, flexShrink: 0, padding: "24px 12px",
        borderRight: `1px solid ${border}`, background: surface,
      }}>
        <div style={{ padding: "0 8px 28px", display: "flex", justifyContent: "center" }}>
          <div style={{
            width: 60, height: 60, borderRadius: "50%",
            background: room.logo_url ? "transparent" : `linear-gradient(135deg, ${brandColor}, #8b7cff)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}>
            {room.logo_url ? (
              <img src={room.logo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 22 }}>{(room.name || "R").slice(0, 1).toUpperCase()}</span>
            )}
          </div>
        </div>
        {room.sections?.map((s) => {
          const isActive = s.id === activeSection;
          return (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              data-testid={`public-section-${s.id}`}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "10px 12px", marginBottom: 4,
                borderRadius: 8, cursor: "pointer",
                background: isActive ? (isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)") : "transparent",
                borderLeft: isActive ? `3px solid ${brandColor}` : "3px solid transparent",
                fontSize: 13, fontWeight: isActive ? 600 : 500,
                color: isActive ? textColor : textMute,
                border: "none", borderLeftWidth: 3,
                borderLeftStyle: "solid",
                borderLeftColor: isActive ? brandColor : "transparent",
              }}
            >
              {s.title}
            </button>
          );
        })}
        <div style={{ position: "fixed", bottom: 20, left: 20, fontSize: 11, color: textMute, opacity: 0.6 }}>
          Built with Pragma Rooms
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, padding: "0 40px 60px", maxWidth: "100%", overflowX: "hidden" }}>
        {section && (
          <div style={{ maxWidth: 860, margin: "0 auto", paddingTop: 24 }}>
            {/* Cover */}
            <div
              style={{
                height: 240, borderRadius: 12, overflow: "hidden",
                background: room.cover_image_url ? undefined : `linear-gradient(135deg, ${brandColor}, #8b7cff)`,
                marginBottom: 30,
              }}
            >
              {room.cover_image_url && (
                <img src={room.cover_image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              )}
            </div>

            {(section.blocks || []).map((block) => (
              <PublicBlock key={block.id} block={block} textColor={textColor} textMute={textMute} border={border} />
            ))}

            {(!section.blocks || section.blocks.length === 0) && (
              <div style={{ padding: 40, textAlign: "center", color: textMute, fontSize: 13 }}>
                This section is empty.
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function EmailGate({ room, onSubmit, submitting }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const brandColor = room.theme_color || "#5B4FE8";
  return (
    <div
      data-testid="room-email-gate"
      style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(135deg, ${brandColor}22, ${brandColor}08)`,
        padding: 20,
      }}
    >
      <div
        style={{
          background: "#fff", borderRadius: 14, padding: "36px 32px",
          width: "min(420px, 100%)", boxShadow: "0 20px 40px rgba(0,0,0,0.10)",
          textAlign: "center",
        }}
      >
        <div style={{
          width: 68, height: 68, borderRadius: "50%",
          background: room.logo_url ? "transparent" : `linear-gradient(135deg, ${brandColor}, #8b7cff)`,
          overflow: "hidden", margin: "0 auto 18px",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {room.logo_url ? <img src={room.logo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> :
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 24 }}>{(room.name || "R").slice(0, 1).toUpperCase()}</span>}
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 6px", color: "#0f172a" }}>{room.name || "Sales room"}</h1>
        <p style={{ color: "#64748b", fontSize: 14, marginBottom: 26, lineHeight: 1.55 }}>
          Enter your email to access this room. We'll use it to keep track of your progress and follow up if you have questions.
        </p>
        <form
          onSubmit={(e) => { e.preventDefault(); if (email.trim()) onSubmit(email.trim(), name.trim()); }}
          style={{ textAlign: "left" }}
        >
          <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "#64748b", marginBottom: 6 }}>
            Full name (optional)
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Alex Buyer"
            data-testid="email-gate-name"
            style={{
              width: "100%", padding: "10px 12px", fontSize: 14, marginBottom: 14,
              border: "1px solid #e5e7eb", borderRadius: 8, outline: "none",
            }}
          />
          <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "#64748b", marginBottom: 6 }}>
            Work email
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            data-testid="email-gate-email"
            style={{
              width: "100%", padding: "10px 12px", fontSize: 14, marginBottom: 20,
              border: "1px solid #e5e7eb", borderRadius: 8, outline: "none",
            }}
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="email-gate-submit"
            style={{
              width: "100%", padding: "12px", fontSize: 14, fontWeight: 600,
              background: brandColor, color: "#fff", border: "none", borderRadius: 8,
              cursor: submitting ? "wait" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Verifying…</> : <><Mail size={14} /> Enter room</>}
          </button>
        </form>
      </div>
    </div>
  );
}

function PublicBlock({ block, textColor, textMute, border }) {
  const c = block.content || {};
  if (block.type === "heading") {
    return <h2 style={{ fontSize: 26, fontWeight: 700, margin: "18px 0 12px", color: textColor }}>{c.text || ""}</h2>;
  }
  if (block.type === "rich_text") {
    return (
      <div
        style={{ fontSize: 15, lineHeight: 1.65, color: textColor, margin: "10px 0" }}
        // Content is authored via our RichTextEditor which already sanitises input on save.
        dangerouslySetInnerHTML={{ __html: c.html || "" }}
      />
    );
  }
  if (block.type === "image") {
    return c.url ? (
      <div style={{ margin: "16px 0" }}>
        <img src={c.url} alt="" style={{ width: "100%", borderRadius: 10, display: "block" }} />
      </div>
    ) : null;
  }
  if (block.type === "file") {
    return c.url ? (
      <a
        href={c.url} target="_blank" rel="noreferrer"
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "10px 14px", border: `1px solid ${border}`, borderRadius: 8,
          textDecoration: "none", color: textColor, fontSize: 13, margin: "12px 0",
        }}
      >
        📎 {c.label || "Attachment"}
      </a>
    ) : null;
  }
  if (block.type === "video_embed") {
    const embed = toEmbedUrl(c.url);
    return embed ? (
      <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, margin: "16px 0", borderRadius: 10, overflow: "hidden", background: "#000" }}>
        <iframe
          src={embed}
          title="Video"
          allow="fullscreen; picture-in-picture; clipboard-write"
          frameBorder="0"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        />
      </div>
    ) : null;
  }
  if (block.type === "divider") {
    return <hr style={{ border: 0, borderTop: `1px solid ${border}`, margin: "22px 0" }} />;
  }
  return null;
}
